import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { NumberParam, StringParam, useQueryParams, withDefault } from 'use-query-params';

import * as routes from '@routes';
import { usePageStore } from '@stores';
import { closeMessage } from '@helpers/messages';
import { useDocumentsApi } from '@api/documents';
import { DocumentState, UserDocument } from '@types';
import { getRequestDateFormat } from '@helpers/datetime';
import { BasePage, Pagination, BasePreloader, AdminBoxContent } from '@components';

import ItemsTable from './components/ItemsTable';
import FiltersForm from './components/FiltersForm';
import RejectModal from './components/RejectModal';
import ApprovedModal from './components/ApprovedModal';

const DocumentsPage: React.FC = () => {
  const location = useLocation();
  const pageState = usePageStore();
  const documentsApi = useDocumentsApi();
  const page = pageState.getPage(routes.admin.DOCUMENTS);
  const [approvedModalDate, setApprovedModalDate] = React.useState<string>();
  const [showApprovedModal, setShowApprovedModal] = React.useState<string | number | undefined>();
  const [documentBeingRejected, setDocumentBeingRejected] = React.useState<UserDocument | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [data, setData] = React.useState<Array<UserDocument>>([]);
  const [dataLen, setDataLen] = React.useState(0);
  const defaultQuery = React.useMemo(
    () => ({
      page: withDefault(NumberParam, 1),
      perPage: withDefault(NumberParam, 15),
      state: withDefault(StringParam, undefined),
      email: withDefault(StringParam, undefined),
      name: withDefault(StringParam, undefined),
      buyerType: withDefault(StringParam, undefined),
    }),
    []
  );
  const [query, setQuery] = useQueryParams(defaultQuery);

  React.useEffect(() => {
    loadData();
    return () => documentsApi.cancelAllRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, query]);

  React.useEffect(() => {
    if (!query.state) {
      setQuery({
        state: DocumentState.uploaded,
      });
    }
    // eslint-disable-next-line
  }, []);

  const loadData = async () => {
    try {
      const response = await documentsApi.list(query);
      setData(response.data.data);
      setDataLen(response.data.total as number);
      setLoaded(true);
    } catch (err) {
      if (!err.response) {
        return;
      }
      setLoaded(true);
    }
  };

  const handleRejectDocument = (item: UserDocument, reason: string) => {
    documentsApi
      .reject(item.id as any, reason)
      .catch(() => closeMessage())
      .then(loadData);
  };

  const handleApprovedDocument = async (validity: Date) => {
    try {
      await documentsApi.approve(showApprovedModal!, getRequestDateFormat(validity));
      setShowApprovedModal(undefined);
      await loadData();
    } catch (err) {
      if (documentsApi.isCancel(err)) {
        return;
      }
    }
  };

  return (
    <BasePage page={routes.admin.DOCUMENTS} title={page?.name}>
      <RejectModal
        title="Zamítnutí"
        isOpen={!!documentBeingRejected}
        onCancel={() => setDocumentBeingRejected(null)}
        onReject={(reason) => {
          handleRejectDocument(documentBeingRejected as any, reason);
          setDocumentBeingRejected(null);
        }}
      />
      {showApprovedModal && (
        <ApprovedModal
          date={approvedModalDate}
          onCancel={() => setShowApprovedModal(undefined)}
          onConfirm={handleApprovedDocument}
        />
      )}
      <FiltersForm values={query} onSubmit={(values) => setQuery(values, 'push')} />
      <AdminBoxContent>
        <Row className="align-items-center">
          <Col xs={12} lg={12} className="text-left text-lg-right mt-4 mt-lg-0">
            {loaded && (
              <p className="m-0">
                Celkem <strong>{dataLen}</strong> dokumentů
              </p>
            )}
          </Col>
        </Row>
        <Row className="mt-3 pt-3">
          <Col xs={12}>
            {loaded ? (
              <div>
                <div>
                  <ItemsTable
                    data={data}
                    onRejectClick={setDocumentBeingRejected}
                    onApproveClick={(item) => {
                      setApprovedModalDate(item.validity?.toString());
                      setShowApprovedModal(item.id);
                    }}
                  />
                </div>
                {data.length > 0 && (
                  <div className="mt-2">
                    <Pagination
                      page={query.page}
                      perPage={query.perPage}
                      totalRows={dataLen}
                      onPageChange={(page) => setQuery({ ...query, page }, 'push')}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="pt-5 pb-5 d-flex align-items-center justify-content-center">
                <BasePreloader />
              </div>
            )}
          </Col>
        </Row>
      </AdminBoxContent>
    </BasePage>
  );
};

export default DocumentsPage;
